/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import {
  Grid,
  Box,
  Container,
  TextField,
  useMediaQuery,
  useTheme,
  FormControlLabel,
  Typography,
  Button,
  InputAdornment,
  IconButton,
} from "@mui/material";
import * as yup from "yup";
import { useFormik } from "formik";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "@store/store";
import { CustomDialog } from "@components/CustomDialog";
import { CustomSwitch } from "@components/CustomSwitch";
import { setModalErrorVisible } from "@store/slices/appSlice";
import {
  useCiecValidatorMutation,
  CiecValidatorRequest,
  BuroValidatorRequestPFAE,
  useBuroValidatorMutation,
} from "@api/sat_apis_new/satApsi";
import { useLazyGetPersonsQuery } from "@api/personasApi";
import { InactivityHandler } from "@components/InactivityHandler";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { CustomTextCiecBuro } from "@components/CustomTextCiecBuro";
import { ApiError } from "@interfaces/response";
import CustomRightImage from "@components/CustomRightImagev2";
import * as Sentry from "@sentry/react";
import { useNavigate } from "react-router-dom";
import { useLazyGetLoanApplicationByIdQuery } from "@api/applications";
import useInterfaceConfig from "@hooks/useInterfaceConfig";
import { TAX_SYSTEM_TYPE } from "@helpers/constantes";
import { truncateText } from "@helpers/funciones";

const ConsultaCiec = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const interfaceConfig = useInterfaceConfig();

  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const inputRef = useRef<HTMLInputElement>(null);

  const companyData = useSelector((state: RootState) => state.register.company);
  const application = useSelector((state: RootState) => state.register.application);
  const isFinbePFAE = interfaceConfig.name === "finbe" && application.tax_system_type === TAX_SYSTEM_TYPE.PFAE.code;

  const [ciecValidation] = useCiecValidatorMutation();
  const [getLoanApplicationById] = useLazyGetLoanApplicationByIdQuery();
  const [getPersonsByLoanId] = useLazyGetPersonsQuery();
  const [buroValidator] = useBuroValidatorMutation();

  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [closeDialog, setCloseDialog] = useState<boolean>(true);
  const [personId, setPersonId] = useState<string>("");
  const [buroSended, setBuroSended] = useState<boolean>(false);

  const validarConsultaBuroPfae = async () => {
    try {
      if (!isFinbePFAE) {
        return;
      }

      const responsePersons = await getPersonsByLoanId({
        loanApplicationId: application.id,
      }).unwrap();

      if (responsePersons.length > 0) {
        setPersonId(responsePersons[0].id as string);
        setBuroSended(responsePersons[0].is_credit_bureau_authorization_pf_sended ?? false);
      }
    } catch (error) {
      console.error("Ha ocurrido un error al validar la consulta al buró de crédito");
    }
  };

  const enviarConsultaBuroPfae = async () => {
    try {
      if (!isFinbePFAE || buroSended) {
        return;
      }

      const legal_name = truncateText(companyData.name || "", 26);
      const first_name = truncateText(companyData.pfae_name || "", 26);
      const first_lastname = truncateText(companyData.pfae_last_name || "", 26);
      const second_lastname = truncateText(companyData.pfae_last_name_2 || "", 26);

      const payload_buro: BuroValidatorRequestPFAE = {
        person_Type: "pf",
        data: {
          rfc: companyData.rfc,
          transaction_number: application.id as string,
          legal_name: legal_name,
          first_name: first_name,
          second_name: "",
          father_last_name: first_lastname,
          mother_last_name: second_lastname,
          reenter_NIP: true,
          authorization_label_response: true,
          loan_application_id: application.id as string,
          is_endorsement: false,
          person_id: personId,
        },
      };

      await buroValidator(payload_buro).unwrap();
    } catch (error) {
      const apiError = error as ApiError;

      Sentry.captureException(error, {
        extra: {
          component: "ConsultaBuro",
          values: {},
        },
      });
      dispatch(setModalErrorVisible({ open: true, error: apiError }));
    }
  };

  const formik = useFormik({
    initialValues: {
      valid_ciec: false,
      ciec: "",
      switch: false,
      switch2: false,
    },
    validationSchema: yup.object().shape({
      ciec: yup.string().when("valid_ciec", {
        is: (value: boolean) => value === false,
        then: (schema) => schema.required("Campo requerido").min(8, "Formato incorrecto, debe tener 8 dígitos"),
      }),
      switch: yup.boolean().when("valid_ciec", {
        is: (value: boolean) => value === false,
        then: (schema) => schema.oneOf([true], "Debe aceptar los términos y condiciones"),
      }),
      switch2: yup.boolean().when("valid_ciec", {
        is: (value: boolean) => value === false,
        then: (schema) => schema.oneOf([true], "Debe aceptar los términos y condiciones"),
      }),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      if (!formik.isValid) return;

      try {
        setSubmitting(true);

        const payload_ciec: CiecValidatorRequest = {
          rfc: companyData.rfc,
          ciec: values.ciec,
          application_code: application.company_application_code,
          client_details_id: String(application.client_details_id),
          loan_application_conditions_id: String(application.application_condition_id),
          loan_application_id: String(application.id),
          requested_amount: Number(application.loan_amount),
          requested_term: Number(application.loan_term),
        };
        await ciecValidation(payload_ciec).unwrap();
        await enviarConsultaBuroPfae();

        navigate("/producto/creditosimple/avance_expediente_digital");
      } catch (error) {
        Sentry.captureException(error, {
          extra: {
            component: "ValidacionTelefono",
            values: {},
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      } finally {
        setSubmitting(false);
      }
    },
    validateOnMount: true,
  });

  const fetchData = async () => {
    try {
      setIsLoading(true);
      if (!application) {
        throw new Error("Solicitud no encontrada");
      }

      const loan_application = await getLoanApplicationById(application.id).unwrap();

      if (!loan_application || !loan_application.is_marketplace_origin) {
        navigate("/producto/creditosimple/avance_expediente_digital");
      }

      await validarConsultaBuroPfae();

      setTimeout(() => {
        formik.validateForm();
      }, 200);
    } catch (error) {
      const api_error = error as ApiError;
      Sentry.captureException(error, {
        extra: {
          component: "AutorizarConsultaBuroPM-fetchData",
        },
      });
      dispatch(setModalErrorVisible({ open: true, error: api_error }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleOnCloseDialog = () => {
    setCloseDialog(true);
  };

  useEffect(() => {
    if (formik.values.switch) {
      setCloseDialog(false);
    } else {
      setCloseDialog(true);
    }
  }, [formik.values.switch]);

  useEffect(() => {
    fetchData();
  }, [application]);

  return (
    <>
      <Grid container height="100%">
        <Grid item xs={12} md={8} mt={2}>
          <InactivityHandler />
          <Box
            sx={{
              mt: isMobileOrTablet ? 1 : 2,
              mx: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Typography
              variant={isMobileOrTablet ? "h4" : "h3"}
              fontWeight={500}
              textAlign="center"
              color={theme?.palette?.primary?.dark}
            >
              Autorización Empresa
            </Typography>

            <Typography variant={isMobileOrTablet ? "body2" : "body1"} color={theme?.palette?.primary?.dark} mt={2}>
              Haremos una consulta al SAT y al buró de crédito del siguiente RFC
            </Typography>

            <Typography
              mt={2}
              variant={isMobileOrTablet ? "body2" : "body1"}
              fontWeight={600}
              color={theme?.palette?.primary?.dark}
              width={isMobileOrTablet ? "90%" : "70%"}
              textAlign={"center"}
            >
              {companyData?.legal_name || "Razón social"}
            </Typography>

            <Typography
              mt={2}
              variant={isMobileOrTablet ? "body2" : "body1"}
              fontWeight={600}
              color={theme?.palette?.primary?.dark}
            >
              {companyData.rfc}
            </Typography>

            <Typography mt={2} variant={isMobileOrTablet ? "body2" : "body1"} color={theme?.palette?.primary?.dark}>
              Ingresa la contraseña
            </Typography>

            <Container maxWidth="sm">
              <form onSubmit={formik.handleSubmit} noValidate>
                <TextField
                  id="ciec"
                  name="ciec"
                  label="Contraseña del SAT (CIEC)"
                  type={showPassword ? "text" : "password"}
                  margin="normal"
                  required
                  fullWidth
                  autoComplete="off"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.ciec}
                  error={formik.touched.ciec && Boolean(formik.errors.ciec)}
                  helperText={formik.touched.ciec && formik.errors.ciec}
                  inputProps={{ maxLength: 8 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          sx={{ color: theme?.palette?.primary?.main }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  disabled={isLoading || formik.isSubmitting}
                  inputRef={inputRef}
                />

                <Box display={"flex"} justifyContent={"center"} mt={isMobileOrTablet ? 1 : 2} textAlign="center">
                  <CustomTextCiecBuro type="textCiec" />
                </Box>

                <Box display={"flex"} justifyContent={"center"} mt={isMobileOrTablet ? 1 : 2} textAlign="center">
                  <FormControlLabel
                    control={<CustomSwitch />}
                    label={
                      <Box sx={{ paddingTop: "10px" }}>
                        <CustomTextCiecBuro type="avisoTerminosTextCiec2" />
                      </Box>
                    }
                    name="switch2"
                    checked={formik.values.switch2}
                    onChange={formik.handleChange}
                    disabled={isLoading || formik.isSubmitting}
                  />
                </Box>

                <Box display={"flex"} justifyContent={"center"} mt={isMobileOrTablet ? 1 : 2} textAlign="center">
                  <FormControlLabel
                    control={<CustomSwitch />}
                    label={
                      <Box sx={{ paddingTop: "10px" }}>
                        <CustomTextCiecBuro type="avisoTerminosTextCiec" />
                      </Box>
                    }
                    name="switch"
                    checked={formik.values.switch}
                    onChange={formik.handleChange}
                    disabled={isLoading || formik.isSubmitting}
                  />
                </Box>

                <Box display={"flex"} justifyContent={"center"} mt={2} mb={2}>
                  <Button
                    type="submit"
                    variant="default"
                    disabled={!formik.isValid || isLoading || formik.isSubmitting}
                  >
                    Continuar
                  </Button>
                </Box>
              </form>
            </Container>
          </Box>
        </Grid>

        {!isMobileOrTablet && (
          <Grid item xs={12} md={4}>
            <CustomRightImage />
          </Grid>
        )}
      </Grid>

      <CustomDialog
        colorClose={theme?.palette?.primary?.light}
        open={!closeDialog}
        onClose={handleOnCloseDialog}
        itemTitle={
          <Typography textAlign={"center"} variant="subtitle1" fontWeight={600} color={theme?.palette?.primary?.dark}>
            Consulta a Buró de crédito
          </Typography>
        }
        itemContent={
          <>
            <CustomTextCiecBuro type="modalCiec" />
          </>
        }
      />
    </>
  );
};

export default ConsultaCiec;
